import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect, useState } from 'react';
import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL, CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import SmartCrmFeatureTable from '../components/feature-table/smart-crm-feature-table/SmartCrmFeatureTable';
import ProductTile from '../components/merch-card/ProductTile';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import MobileProductLevelSelectorButtons from '../components/mobile/MobileProductLevelSelectorButtons';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { STANDALONE_CORE_SEATS } from '../constants/ReleaseInsights';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { SMART_CRM_CARD } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useHasReleaseInsight } from '../hooks/useBulkPurchaseOptions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_LEVELS_MAP = {
  [HUBSPOT_FREE]: FREE,
  [CRM_SUITE_STARTER_BUNDLE]: STARTER,
  [CORE_SEATS_PROFESSIONAL]: PROFESSIONAL,
  [CORE_SEATS_ENTERPRISE]: ENTERPRISE
};
const PUBLIC_PRODUCTS_TO_SHOW = [HUBSPOT_FREE, CRM_SUITE_STARTER_BUNDLE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE];
const I18N_KEY_OVERRIDE_MAP = {
  [CRM_SUITE_STARTER_BUNDLE]: 'merchCards.suiteTile.starterCard',
  [HUBSPOT_FREE]: 'merchCards.suiteTile.freeCard'
};
const MobileSmartCrmPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('smart-crm');
  const screen = SCREENS.SMART_CRM;
  const navItem = NavItemEnum.SMART_CRM;
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const isAppReady = useGlobalLoading();
  const {
    hasReleaseInsight: isUngatedForSmartCrm,
    loading: isLoadingReleaseInsight,
    error: isErrorLoadingReleaseInsight
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const productsToShow = PUBLIC_PRODUCTS_TO_SHOW;
  const productLevelsToShow = productsToShow.map(apiName => PRODUCT_LEVELS_MAP[apiName]);
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevelsToShow[0]);
  const [selectedProduct, setSelectedProduct] = useState(productsToShow[0]);
  const upgradeProduct = ApiNameToUpgradeProductMap[selectedProduct];
  const redirectCondition = () => {
    if (isLoadingReleaseInsight) return;
    return !isUngatedForSmartCrm || !!isErrorLoadingReleaseInsight;
  };
  useRedirectIfIneligible({
    condition: redirectCondition
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const onClickProductLevelSelectorButton = productLevel => {
    setSelectedProductLevel(productLevel);
    setSelectedProduct(productsToShow[productLevelsToShow.indexOf(productLevel)]);
  };
  if (hasPricingAndPurchasingFailed && hasOnlyOwnershipCheckFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: OWNERSHIP_CHECK_FAILED
    });
  }
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const gridItemProps = {
    gridColumnStart: 1,
    paddingInline: '700',
    showBorderLeft: true,
    showBorderRight: true
  };
  return /*#__PURE__*/_jsxs(MobileWrapper, {
    navItem: navItem,
    navMarkerName: "SMART_CRM_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: screen
    }), /*#__PURE__*/_jsx(FailedLoadBanner, {
      hasFailed: hasPricingAndPurchasingFailed
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
      productLevels: productLevelsToShow,
      onClick: onClickProductLevelSelectorButton,
      selectedProductLevel: selectedProductLevel
    }), /*#__PURE__*/_jsxs(PageSection, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockStart: "500",
        children: /*#__PURE__*/_jsx(CardGrid, {
          columns: 1,
          rowGap: "0",
          children: /*#__PURE__*/_jsx(ProductTile, {
            apiName: selectedProduct,
            screen: screen,
            trackingSource: SMART_CRM_CARD,
            i18nKeyOverride: I18N_KEY_OVERRIDE_MAP[selectedProduct],
            productNameOverride: navItem,
            gridItemProps: gridItemProps
          })
        })
      }), /*#__PURE__*/_jsx(SmartCrmFeatureTable, {
        productLevels: [selectedProductLevel],
        screen: screen
      })]
    }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
      phoneSourceId: "smart-crm-footer",
      screen: screen,
      upgradeProduct: upgradeProduct
    })]
  });
};
export default MobileSmartCrmPage;